import { useEffect, useMemo, useRef, useState } from "react"
import { DateTime, Info } from "luxon"
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react"

// Day picker used by every report header. Speaks the same "day offset from
// today" (0 = today, -1 = yesterday) the reports already use, in the
// beneficiary's time zone, so swapping it in for the old 30-entry <select>
// changes nothing downstream. Opens as an anchored popover on desktop and a
// bottom sheet on phones.

export interface DatePickerProps {
    value: number
    onChange: (offset: number) => void
    timezone: string
    // How far back a day can be picked, in days.
    maxDaysBack?: number
}

const MOBILE_QUERY = '(max-width: 639px)'

function triggerLabel(date: DateTime, today: DateTime): string {
    const offset = Math.round(date.diff(today, 'days').days)
    if (offset === 0) return 'Today'
    if (offset === -1) return 'Yesterday'
    return date.year === today.year ? date.toFormat('EEE, MMM d') : date.toFormat('MMM d, yyyy')
}

export function DatePicker({ value, onChange, timezone, maxDaysBack = 365 }: DatePickerProps) {
    const [open, setOpen] = useState(false)
    const [view, setView] = useState<'days' | 'months'>('days')
    const containerRef = useRef<HTMLDivElement>(null)

    // Recomputed on open as well, so a page left open past midnight moves on.
    const today = useMemo(() => DateTime.now().setZone(timezone).startOf('day'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [timezone, open])
    const minDate = today.minus({ days: maxDaysBack })
    const selected = today.plus({ days: value })
    const [viewMonth, setViewMonth] = useState<DateTime>(selected.startOf('month'))

    useEffect(() => {
        if (!open) return
        setViewMonth(selected.startOf('month'))
        setView('days')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // Outside click / Escape close the popover; on phones the sheet also
    // locks page scroll so the dashboard doesn't move underneath it.
    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent | TouchEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('touchstart', onDown)
        document.addEventListener('keydown', onKey)
        const lockScroll = window.matchMedia?.(MOBILE_QUERY).matches
        const prevOverflow = document.body.style.overflow
        if (lockScroll) document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('touchstart', onDown)
            document.removeEventListener('keydown', onKey)
            if (lockScroll) document.body.style.overflow = prevOverflow
        }
    }, [open])

    const pick = (date: DateTime) => {
        onChange(Math.round(date.startOf('day').diff(today, 'days').days))
        setOpen(false)
    }

    const step = (delta: number) => {
        const next = value + delta
        if (next > 0 || next < -maxDaysBack) return
        onChange(next)
    }

    const startOfWeek = Info.getStartOfWeek()
    // Info.weekdays is Monday-first; rotate to the locale's first day.
    const weekdayNames = useMemo(() => {
        const names = Info.weekdays('narrow')
        return [...names.slice(startOfWeek - 1), ...names.slice(0, startOfWeek - 1)]
    }, [startOfWeek])

    const days = useMemo(() => {
        const lead = (viewMonth.weekday - startOfWeek + 7) % 7
        const first = viewMonth.minus({ days: lead })
        const count = Math.ceil((lead + viewMonth.daysInMonth!) / 7) * 7
        return Array.from({ length: count }, (_, i) => first.plus({ days: i }))
    }, [viewMonth, startOfWeek])

    const months = useMemo(() => {
        const out: DateTime[] = []
        for (let m = minDate.startOf('month'); m <= today; m = m.plus({ months: 1 })) out.push(m)
        return out
    }, [minDate, today])

    const canPrevMonth = viewMonth > minDate.startOf('month')
    const canNextMonth = viewMonth < today.startOf('month')

    const navBtn = 'h-9 w-9 flex items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent'

    return (
        <div ref={containerRef} className="relative shrink-0">
            <div className="flex items-center rounded-lg border border-gray-300 bg-gray-100 text-sm text-gray-800">
                <button type="button" aria-label="Previous day" className="h-9 w-8 flex items-center justify-center rounded-l-lg hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                    disabled={value <= -maxDaysBack} onClick={() => step(-1)}>
                    <ChevronLeft size={16} />
                </button>
                <button type="button" aria-haspopup="dialog" aria-expanded={open}
                    className="h-9 px-2 flex items-center gap-1.5 border-x border-gray-300 hover:bg-gray-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                    onClick={() => setOpen(o => !o)}>
                    <CalendarDays size={16} className="hidden min-[400px]:block text-gray-600" />
                    <span className="font-medium">{triggerLabel(selected, today)}</span>
                    <ChevronDown size={14} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                <button type="button" aria-label="Next day" className="h-9 w-8 flex items-center justify-center rounded-r-lg hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                    disabled={value >= 0} onClick={() => step(1)}>
                    <ChevronRight size={16} />
                </button>
            </div>

            {open && (
                <>
                    {/* Phone backdrop; the sheet sits above it. */}
                    <div className="fixed inset-0 z-[1100] bg-black/30 sm:hidden" onClick={() => setOpen(false)} />
                    <div role="dialog" aria-label="Choose a date"
                        className="fixed inset-x-0 bottom-0 z-[1101] rounded-t-2xl bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl
                                   sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:rounded-xl sm:border sm:border-gray-200 sm:pb-4 sm:shadow-lg">
                        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300 sm:hidden" />

                        <div className="flex items-center justify-between mb-2">
                            <button type="button" aria-label="Previous month" className={navBtn}
                                disabled={view === 'months' || !canPrevMonth} onClick={() => setViewMonth(m => m.minus({ months: 1 }))}>
                                <ChevronLeft size={18} />
                            </button>
                            <button type="button" className="px-3 h-9 rounded-lg text-sm font-semibold text-gray-800 hover:bg-gray-100 flex items-center gap-1"
                                onClick={() => setView(v => v === 'days' ? 'months' : 'days')}>
                                {view === 'days' ? viewMonth.toFormat('LLLL yyyy') : 'Pick a month'}
                                <ChevronDown size={14} className={`text-gray-500 transition-transform ${view === 'months' ? 'rotate-180' : ''}`} />
                            </button>
                            <div className="flex items-center">
                                <button type="button" aria-label="Next month" className={navBtn}
                                    disabled={view === 'months' || !canNextMonth} onClick={() => setViewMonth(m => m.plus({ months: 1 }))}>
                                    <ChevronRight size={18} />
                                </button>
                                <button type="button" aria-label="Close" className={`${navBtn} sm:hidden`} onClick={() => setOpen(false)}>
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {view === 'days' ? (
                            <div className="grid grid-cols-7 gap-y-1 text-center">
                                {weekdayNames.map((d, i) => (
                                    <div key={i} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">{d}</div>
                                ))}
                                {days.map(d => {
                                    const inMonth = d.month === viewMonth.month
                                    const disabled = d > today || d < minDate
                                    const isSelected = d.hasSame(selected, 'day')
                                    const isToday = d.hasSame(today, 'day')
                                    let cls = 'mx-auto h-10 w-10 sm:h-9 sm:w-9 rounded-full text-sm flex items-center justify-center transition-colors '
                                    if (isSelected) cls += 'bg-blue-500 text-white font-semibold'
                                    else if (disabled) cls += 'text-gray-300 cursor-default'
                                    else cls += `${inMonth ? 'text-gray-800' : 'text-gray-400'} hover:bg-gray-100 ${isToday ? 'ring-1 ring-blue-500 font-semibold text-blue-600' : ''}`
                                    return (
                                        <button key={d.toISODate()} type="button" disabled={disabled} className={cls}
                                            aria-label={d.toFormat('DDDD')} aria-pressed={isSelected}
                                            onClick={() => pick(d)}>
                                            {d.day}
                                        </button>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {months.map(m => {
                                    const active = m.hasSame(viewMonth, 'month')
                                    return (
                                        <button key={m.toISODate()} type="button"
                                            className={`h-11 rounded-lg text-sm ${active ? 'bg-blue-500 text-white font-semibold' : 'bg-gray-50 text-gray-800 hover:bg-gray-100'}`}
                                            onClick={() => { setViewMonth(m); setView('days') }}>
                                            {m.toFormat('LLL')}
                                            <span className={`block text-[10px] leading-none ${active ? 'text-blue-100' : 'text-gray-500'}`}>{m.year}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        <div className="mt-3 pt-3 border-t flex gap-2">
                            {[{ label: 'Today', offset: 0 }, { label: 'Yesterday', offset: -1 }, { label: 'A week ago', offset: -7 }].map(q => (
                                <button key={q.label} type="button"
                                    className={`flex-1 h-9 rounded-lg text-xs font-medium ${value === q.offset ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                    onClick={() => pick(today.plus({ days: q.offset }))}>
                                    {q.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
